import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Button, Callout, Checkbox, Dialog, Field, Select, TextArea, TextInput } from '../../../ui';
import { Genre, LeadSource, SOURCE_LABELS, createCandidate, errorText } from '../../../TeacherHiring/hiringApi';

interface Props {
  open: boolean;
  genres: Genre[];
  onClose: () => void;
  onAdded: (id: string) => void;
}

const SOURCES: LeadSource[] = ['OUTBOUND', 'WEBSITE', 'META', 'JOB_PORTAL', 'REFERRAL', 'OTHER'];

const empty = { name: '', phone: '', email: '', city: '', genre: '', experienceYears: '', source: 'OUTBOUND' as LeadSource, sourceDetail: '', notes: '' };

/** Adding a lead by hand: outbound calls, walk-ins, a referral that came by phone. */
const AddCandidateDialog: React.FC<Props> = ({ open, genres, onClose, onAdded }) => {
  const [form, setForm] = useState(empty);
  const [autoScreen, setAutoScreen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(empty);
      setAutoScreen(true);
      setError(null);
    }
  }, [open]);

  const set = (key: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async () => {
    if (form.name.trim().length < 2 || form.phone.trim().length < 6) {
      setError('Name and phone are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { id, created } = await createCandidate({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        city: form.city.trim() || undefined,
        genres: form.genre ? [form.genre] : undefined,
        experienceYears: form.experienceYears ? Number(form.experienceYears) : undefined,
        source: form.source,
        sourceDetail: form.sourceDetail.trim() || undefined,
        notes: form.notes.trim() || undefined,
        autoScreen,
      });
      toast[created ? 'success' : 'info'](created ? `${form.name} added` : 'Already in the pipeline; opened their profile');
      onAdded(id);
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      persistent
      size="lg"
      title="Add candidate"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} loading={saving}>Add candidate</Button>
        </>
      }
    >
      <div className="th-form">
        <div className="th-form__row">
          <Field label="Name" required>{(ctl) => <TextInput {...ctl} value={form.name} onChange={set('name')} autoFocus />}</Field>
          <Field label="Phone" required hint="10-digit Indian numbers need no +91.">
            {(ctl) => <TextInput {...ctl} type="tel" value={form.phone} onChange={set('phone')} />}
          </Field>
        </div>
        <div className="th-form__row">
          <Field label="Email">{(ctl) => <TextInput {...ctl} type="email" value={form.email} onChange={set('email')} />}</Field>
          <Field label="City">{(ctl) => <TextInput {...ctl} value={form.city} onChange={set('city')} />}</Field>
        </div>
        <div className="th-form__row">
          <Field label="Genre">
            {(ctl) => (
              <Select {...ctl} value={form.genre} onChange={set('genre')}
                options={[{ value: '', label: 'Not known yet' }, ...genres.filter((g) => g.active).map((g) => ({ value: g.id, label: g.name }))]} />
            )}
          </Field>
          <Field label="Years teaching">
            {(ctl) => <TextInput {...ctl} numeric type="number" min={0} max={70} value={form.experienceYears} onChange={set('experienceYears')} />}
          </Field>
        </div>
        <div className="th-form__row">
          <Field label="Source">
            {(ctl) => <Select {...ctl} value={form.source} onChange={set('source')} options={SOURCES.map((s) => ({ value: s, label: SOURCE_LABELS[s] }))} />}
          </Field>
          <Field label="Source detail" hint="Campaign, portal or who referred them.">
            {(ctl) => <TextInput {...ctl} value={form.sourceDetail} onChange={set('sourceDetail')} />}
          </Field>
        </div>
        <Field label="Notes">{(ctl) => <TextArea {...ctl} value={form.notes} onChange={set('notes')} rows={2} />}</Field>
        <Checkbox
          checked={autoScreen}
          onChange={(e) => setAutoScreen(e.target.checked)}
          label="Start the AI screening call"
          hint="Off keeps them in New lead until you move them."
        />
        {error && <Callout tone="danger">{error}</Callout>}
      </div>
    </Dialog>
  );
};

export default AddCandidateDialog;
